<template>
  <section class="chart-statistics-container">
    <el-container class="chart-statistics package-title">
      <el-main>
        <h1>{{ $t('addressesTile.title') }}</h1>
      </el-main>
    </el-container>

    <div class="chart-statistics">
      <TileRowDivider
        :column-label01="$t('addressesTile.packageType.hgv')"
        :column-label02="$t('addressesTile.packageType.libert')"
        :column-label03="$t('addressesTile.packageType.obu')"
      />

      <!------------------------- AS24 START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="as24Logo" />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.as24Hgv"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.as24Libert"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.as24Obu"
          />
        </div>
      </div>

      <!-------------------------- AS24 END -------------------------->

      <TileRowDivider />

      <!------------------------- TOTAL START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="totalLogo" />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.totalEnergiesHgv"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.totalEnergiesLibert"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.totalEnergiesObu"
          />
        </div>
      </div>

      <!------------------------- TOTAL END ------------------------->

      <TileRowDivider />

      <!------------------------- CIRCLE-K START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="circlekLogo" />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.circleKHgv"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.circleKLibert"
          />
        </div>

        <div
          v-loading="queryLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions()"
            :series="addressCorrectionDonutSeries.circleKObu"
          />
        </div>
      </div>

      <!------------------------- CIRCLE-K END ------------------------->

      <TileRowDivider />

      <!------------------------- INFO START ------------------------->

      <div class="bottom-info-container">
        <div class="bottom-info">
          <el-row>
            <el-col
              :span="24"
              class="colors-info"
            >
              <el-tag>
                <span class="coloured-dot red" />
                <span>{{ $t('addressesTile.tileShortStatistics.awaitingCorrectionForTwoDaysOrMore') }}</span>
              </el-tag>

              <el-tag>
                <span class="coloured-dot yellow" />
                <span>{{ $t('addressesTile.tileShortStatistics.awaitingCorrectionForLessThanTwoDays') }}</span>
              </el-tag>

              <el-tag>
                <span class="coloured-dot green" />
                <span>{{ $t('addressesTile.tileShortStatistics.awaitingCorrectionToday') }}</span>
              </el-tag>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>

    <!------------------------- INFO END ------------------------->
  </section>
</template>

<script lang="ts">
import { Vue } from 'vue-property-decorator'
import Component from 'vue-class-component'

import {
  Maybe,
  AddressCorrectionTileStatisticsQuery,
  AddressCorrectionTileStatistics,
  AddressCorrectionTileCategory,
  AddressCorrectionTileInfo
} from '@/types/graphql'
import { ApolloQueryResult } from 'apollo-client'
import TileRowDivider from '@/components/UI/TileRowDivider.vue'

@Component({
  components: {
    TileRowDivider
  }
})

export default class OrdersTile extends Vue {
  // State

  private queryLoading = false

  private queryResult: Maybe<AddressCorrectionTileStatistics> = null

  // Computed getters

  private getSeriesWithEmptyState(older: number, recent: number, today: number): number[] {
    const hasData = older + recent + today > 0
    // When no data, return [0, 0, 0, 1] - the 4th value will be gray
    return hasData ? [older, recent, today] : [0, 0, 0, 1]
  }

  private get addressCorrectionDonutSeries() {
    const emptyState = [0, 0, 0, 1]
    const result: Record<string, number[]> = {
      as24Hgv: emptyState,
      as24Libert: emptyState,
      as24Obu: emptyState,

      totalEnergiesHgv: emptyState,
      totalEnergiesLibert: emptyState,
      totalEnergiesObu: emptyState,

      circleKHgv: emptyState,
      circleKLibert: emptyState,
      circleKObu: emptyState
    }

    if (!this.queryResult) {
      return result
    }

    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    (Object.entries(this.queryResult) as [keyof AddressCorrectionTileStatistics, AddressCorrectionTileCategory][])
      .forEach(([entity, entityData]) => {
        if (entity !== '__typename') {
          (Object.entries(entityData) as [keyof AddressCorrectionTileCategory, AddressCorrectionTileInfo][])
            .forEach(([flow, flowData]) => {
              if (flow !== '__typename') {
                const key = `${this.toCamelCase(entity)}${capitalize(flow)}` as keyof typeof result
                result[key] = this.getSeriesWithEmptyState(flowData.older, flowData.recent, flowData.today)
              }
            })
        }
      })

    return result
  }

  private toCamelCase(str: string) {
    return str.toLowerCase().replace(/_([a-z])/g, (_, letter) => {
      return letter.toUpperCase()
    })
  }

  private getDonutOptions() {
    return {
      chart: {
        width: '75%',
        type: 'donut',
        animations: {
          enabled: false
        }
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      colors: ['hsl(0, 100%, 50%)', 'hsl(47, 100%, 50%)', 'hsl(161, 67%, 47%)', 'hsl(222, 32%, 92%)'],
      plotOptions: {
        pie: {
          customScale: 1,
          expandOnClick: false,
          donut: {
            size: '87%',
            labels: {
              show: true,
              name: {
                show: false
              },
              value: {
                show: true,
                fontSize: '16px',
                offsetY: -12
              },
              total: {
                show: true,
                showAlways: true,
                label: '',
                formatter: function(w: any) {
                  return w.globals.seriesTotals.slice(0, 3)
                }
              }
            }
          }
        }
      },
      stroke: { show: true, width: 0 },
      tooltip: {
        enabled: false
      },
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      }
    }
  }

  // Methods

  private async fetchAddressCorrectionTileStatistics() {
    this.queryLoading = true

    try {
      const result: ApolloQueryResult<AddressCorrectionTileStatisticsQuery> = await this.$apollo.query({
        query: AddressCorrectionTileStatisticsQuery
      })

      this.queryResult = result.data.addressCorrectionTileStatistics
    } catch (error) {
      console.error('[fetchAddressCorrectionTileStatistics] ERROR => ' + error)
    }

    this.queryLoading = false
  }

  // Hooks

  private mounted() {
    this.fetchAddressCorrectionTileStatistics()
  }

  public as24Logo = require('@/assets/AS24_tile_logo.png');
  public totalLogo = require('@/assets/TotalEnergies_tile_logo.png');
  public circlekLogo = require('@/assets/circlek_logo.jpg');
}
</script>

<style lang="scss">
  @import "@/css/chartStatisticsTile.scss";
</style>
